import {
  Points,
  BufferGeometry,
  PointsMaterial,
  Vector3,
  Raycaster,
  BufferAttribute,
  Box3Helper,
  Color,
  Texture,
  Group,
  Box2,
  Ray,
} from "three";

import { RaycasterSettings, ITextScale } from "../config/ViewerConfig";
import { getRoundRect } from "../measurement/utils";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { PolygonUtils } from "../utils/math-utils";
import { IPointCloudTileGeometry } from "./loader/interface";
import { IPointCloudTile, IPointColorRangeInfo } from "./PointCloudTile";
import { PointCloudMaterial } from "../utils/PointCloudMaterial";

export interface IPointcloudSectionSelection {
  convexShapes: {
    planes: {
      position: Vector3;
      normal: Vector3;
    }[];
    isSelectInside: boolean;
    isDeleted: boolean;
  }[];
  shapeCounts: number[];
}

export class PointCloud {
  private _points: Points;
  private _geometry: BufferGeometry;
  private _material: PointCloudMaterial;
  private _pickMaterial: PointCloudMaterial;
  private _originalColors: Float32Array;
  private _currentColors: Float32Array;

  private _isOriginalColorUsed: boolean;

  private _threshold: number;

  private _offset: Vector3;

  private _debug: Group;
  private _box3: Box3Helper;
  private _textSprite: AutoAttenuatableTextSprite;
  private _textTextureContext: CanvasRenderingContext2D;
  private _textTexture: Texture;

  // private _isHeatMapVisible: boolean;

  //
  constructor(
    textCanvas: HTMLCanvasElement,
    material: PointCloudMaterial,
    textScale: ITextScale,
    // settings?: PointCloudSettings,
    raycasterSettings?: RaycasterSettings,
    tile?: IPointCloudTile,
    pickMaterial?: PointCloudMaterial
  ) {
    this._points = new Points();
    this._geometry = new BufferGeometry();

    this._box3 = new Box3Helper(tile.boundingBox, new Color(0xffff00));
    // this._box3 = new Box3Helper(tile.tileBBox, new Color(0x00ffff));

    // this._material = new PointsMaterial({
    //   vertexColors: true,
    //   size: settings?.size || 4,
    //   sizeAttenuation: false,
    // });
    // this._material = new PointCloudMaterial({
    //   vertexColors: true,
    //   size: settings?.size || 4,
    //   sizeAttenuation: false,
    //   transparent: true,
    // });

    this._material = material;
    this._pickMaterial = pickMaterial;

    this._isOriginalColorUsed = true;

    // this._isHeatMapVisible = true;

    // this._material = new PointMaterialVolume({
    //   vertexColors: true,
    //   size: settings?.size || 4,
    //   sizeAttenuation: false,
    // });

    textCanvas.width = 1000;
    textCanvas.height = 500;
    this._textTextureContext = textCanvas.getContext("2d");
    this._textTexture = new Texture(textCanvas);
    this._textTexture.needsUpdate = true;
    this._textSprite = new AutoAttenuatableTextSprite(
      this._textTexture,
      textScale
    );
    this._textSprite.visible = true;

    this.updateTextContext2d(tile.url);

    tile.boundingBox.getCenter(this._textSprite.position);

    this._debug = new Group();
    this._debug.add(this._textSprite);
    this._debug.add(this._box3);
    this._threshold = raycasterSettings?.threshold || 0.01;
    this._offset = new Vector3(0, 0, 0);
  }

  public dispose() {
    this._points?.removeFromParent();
    this._geometry.dispose();
    this._geometry = undefined;
    // this._material.dispose();
    // this._material = undefined;
    this._points = undefined;
    this._box3.parent.remove(this._box3);
    this._box3.geometry.dispose();
    this._box3.geometry = undefined;
    this._box3.material = undefined;
    this._box3 = undefined;
    this._textSprite.dispose();
    this._debug.removeFromParent();
  }

  public getPointsMaterial(): PointsMaterial {
    return this._material;
  }

  public getMesh(): Points {
    // console.log("this._points.geometry position: ", this._points.geometry.getAttribute('position').array.length);
    return this._points;
  }

  public setPickMaterial(isPick: boolean): void {
    if (isPick) {
      this._points.material = this._pickMaterial;
    } else {
      this._points.material = this._material;
    }
  }

  // public setPickMaterialIndex(index: number): void {
  //   console.log('needsUpdate')
  //   // this._pickMaterial.tileIndex = index;
  //   // this._pickMaterial.needsUpdate = true;
  //   // (this._points.material as Material).needsUpdate = true;
  //   console.log(index);
  //   this._pickMaterial = new PointCloudMaterial({
  //     vertexColors: true,
  //     // size: size || 4,
  //     size: 10,
  //     sizeAttenuation: false,
  //     transparent: false,
  //   }
  //     , "indices"
  //     , index
  //   )
  //   this._points.material = this._pickMaterial;
  // }

  public getDebug(): Group {
    return this._debug;
  }

  public getOffset(): Vector3 {
    return this._offset;
  }

  public setGeometry(
    points: Float32Array,
    colors: Float32Array,
    indices?: Uint8Array
  ) {
    this._geometry.setAttribute("position", new BufferAttribute(points, 3));
    if (indices) {
      this._geometry.setAttribute(
        "indices",
        new BufferAttribute(indices, 3, true)
      );
    }
    this._originalColors = colors;
    // console.log(indices);
    // console.log(colors);
    this._geometry.setAttribute(
      "color",
      new BufferAttribute(this._originalColors, 3)
    );

    this._points.geometry = this._geometry;
    this._points.material = this._material;
    this._points.frustumCulled = false;

    this._points.geometry.computeBoundingBox();
    this._points.geometry.computeBoundingSphere();
  }

  public intersect(raycaster: Raycaster): Vector3 {
    const result = new Vector3();
    if (raycaster.params.Points !== undefined) {
      raycaster.params.Points.threshold = this._threshold;
    }
    let intersects = raycaster.intersectObject(this._points);

    this._points.geometry.computeBoundingBox();
    this._points.geometry.computeBoundingSphere();
    if (intersects.length === 0) return null;
    return result.set(
      intersects[0].point.x,
      intersects[0].point.y,
      intersects[0].point.z
    );
  }

  private updateTextContext2d(text: string) {
    const canvasWidth = this._textTextureContext.canvas.width;
    const canvasHeight = this._textTextureContext.canvas.height;
    this._textSprite.geometry.computeBoundingBox();
    this._textTextureContext.clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    // const text = `${this.value.toFixed(3)} m`;
    // const measurementId = `1`;

    this._textTextureContext.font = fontsize + "px " + fontface;

    // const measurementIdMetrics =
    //   this._textTextureContext.measureText(measurementId);
    // const measurementIdTextCirleRadius = measurementIdMetrics.width * 0.5 + 10;
    const measurementIdTextCirleRadius = 15;
    // const text = `${this.distance} m`;

    const metrics = this._textTextureContext.measureText(text);
    const textWidth = metrics.width;

    this._textTextureContext.lineWidth = borderThickness;
    this._textTextureContext.fillStyle = "rgba(0,0,0,0.7)"; //
    this._textTextureContext.strokeStyle = "rgba(0,0,0,1.0)"; //
    getRoundRect(
      this._textTextureContext,
      borderThickness / 2,
      borderThickness / 2,
      measurementIdTextCirleRadius * 2 + 8 + textWidth + borderThickness,
      measurementIdTextCirleRadius * 2 + 8,
      6
    );

    this._textTextureContext.fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._textTextureContext.strokeStyle = "rgba(255,255,255,1.0)"; //text color

    // this._textTextureContext.beginPath();
    // this._textTextureContext.arc(
    //   measurementIdTextCirleRadius + 2,
    //   measurementIdTextCirleRadius + 2,
    //   measurementIdTextCirleRadius,
    //   0,
    //   2 * Math.PI
    // );
    // this._textTextureContext.stroke();

    const textY = measurementIdTextCirleRadius + fontsize * 0.4;
    // this._textTextureContext.fillText(measurementId, 9, textY);

    this._textTextureContext.fillText(text, 8, textY);

    const newCanvasWidth = Math.ceil(
      measurementIdTextCirleRadius * 2 + 8 + textWidth + 2 * borderThickness
    );
    const newCanvasHeight = Math.ceil(fontsize * 1.4 + 2 * borderThickness);

    const geom = this._textSprite.geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._textSprite.aspect = newCanvasHeight / newCanvasWidth;
  }

  public applyHeightBasedColor(
    colorData: IPointColorRangeInfo,
    polygon: { vertices: Vector3[]; indices: number[] },
    polygonbbox: Box2
  ) {
    const ray = new Ray(new Vector3(), new Vector3(0, 0, 1));
    const getHeightDifferenceToPolygonMesh = (point: Vector3) => {
      ray.origin.copy(point);
      let hit = PolygonUtils.intersectionTest(polygon, ray);
      if (!hit) {
        ray.direction.negate();
        hit = PolygonUtils.intersectionTest(polygon, ray);
      }
      if (hit) {
        return point.z - hit.z;
      }
    };
    const memoPoint = new Vector3();
    const memoColor = new Color();
    const points = this._geometry.getAttribute("position")
      .array as Float32Array;
    // save the original for reset
    // edit the
    // if (this._originalColors === undefined) {
    //   this._currentColors = this._geometry.getAttribute("color")
    //     .array as Float32Array;
    //   this._originalColors = this._currentColors.slice();
    // }
    this._currentColors = this._originalColors.slice();

    for (let i = 0; i < points.length; i += 3) {
      memoPoint.set(
        points[i], //+ offsetMatrix.elements[12],
        points[i + 1], // + offsetMatrix.elements[13],
        points[i + 2] // + offsetMatrix.elements[14]
      );
      if (
        PolygonUtils.isPointInsidePolygon2D(
          memoPoint.x,
          memoPoint.y,
          polygon.vertices,
          polygonbbox
        )
      ) {
        const height = getHeightDifferenceToPolygonMesh(memoPoint);
        if (height !== undefined) {
          if (height > 0) {
            memoColor.lerpColors(
              colorData.upper.min.color,
              colorData.upper.max.color,
              height / colorData.upper.max.val
            );
            if (
              isNaN(memoColor.r) ||
              isNaN(memoColor.g) ||
              isNaN(memoColor.b)
            ) {
              memoColor.setRGB(1, 1, 1);
            }
            //upperbound
            this._currentColors[i] = memoColor.r;
            this._currentColors[i + 1] = memoColor.g;
            this._currentColors[i + 2] = memoColor.b;
          } else {
            memoColor.lerpColors(
              colorData.lower.min.color,
              colorData.lower.max.color,
              height / colorData.lower.max.val
            );
            if (
              isNaN(memoColor.r) ||
              isNaN(memoColor.g) ||
              isNaN(memoColor.b)
            ) {
              memoColor.setRGB(1, 1, 1);
            }
            //lowerbound
            this._currentColors[i] = memoColor.r;
            this._currentColors[i + 1] = memoColor.g;
            this._currentColors[i + 2] = memoColor.b;
          }
        }
      } else {
        //color should be as it is
      }
      // if (this._isHeatMapVisible) {
      this._geometry.setAttribute(
        "color",
        new BufferAttribute(this._currentColors, 3)
      );
      this._geometry.getAttribute("color").needsUpdate = true;
      // }
      this._isOriginalColorUsed = false;
    }
  }

  public resetColor() {
    if (!this._isOriginalColorUsed) {
      this._geometry.setAttribute(
        "color",
        new BufferAttribute(this._originalColors, 3)
      );
      this._geometry.getAttribute("color").needsUpdate = true;
      this._isOriginalColorUsed = true;
    }
  }

  public setHeatMapVisibility(visibility: boolean) {
    // this._isHeatMapVisible = visibility;
    if (visibility) {
      if (!this._currentColors) {
        this._currentColors = this._geometry.getAttribute("color")
          .array as Float32Array;
      }
      this._geometry.setAttribute(
        "color",
        new BufferAttribute(this._currentColors, 3)
      );
      this._geometry.getAttribute("color").needsUpdate = true;
      this._isOriginalColorUsed = false;
    } else {
      // if (!this._originalColors) {
      //   this._originalColors = this._geometry.getAttribute("color")
      //     .array as Float32Array;
      // }
      this._geometry.setAttribute(
        "color",
        new BufferAttribute(this._originalColors, 3)
      );
      this._geometry.getAttribute("color").needsUpdate = true;
      this._isOriginalColorUsed = true;
    }
  }

  public setVisibility(visibility: boolean) {
    this._points.visible = visibility;
  }

  public setPointSize(size: number) {
    this._material.size = size;
    this._pickMaterial.size = size;
  }

  // public highlightSelectedPointCloudSection(
  //   selection: IPointcloudSectionSelection,
  //   color: Color
  // ) {
  // const isPointInsidePlanes = (point: Vector3) => {
  //   for (const convexShape of selection.convexShapes) {
  //     let isInside = true;
  //     for (let i = 0; i < convexShape.planes.length; ++i) {
  //       const plane = new Plane().setFromNormalAndCoplanarPoint(
  //         convexShape.planes[i].normal,
  //         convexShape.planes[i].position
  //       );
  //       const projectedPoint = plane.projectPoint(point, new Vector3());
  //       const dir = new Vector3().subVectors(point, projectedPoint);
  //       if (dir.dot(plane.normal) < 0) {
  //         isInside = false;
  //       }
  //     }
  //     if (isInside) {
  //       return true;
  //     }
  //   }
  //   return false;
  // };

  // const points = this._geometry.getAttribute("position")
  //   .array as Float32Array;
  // this._currentColors = this._originalColors.slice();

  // const memoPoint = new Vector3();
  // const direction = selectionPlanes[0].normal;
  // for (let i = 0; i < points.length; i += 3) {
  //   memoPoint.set(
  //     points[i], //+ offsetMatrix.elements[12],
  //     points[i + 1], // + offsetMatrix.elements[13],
  //     points[i + 2] // + offsetMatrix.elements[14]
  //   );
  //   if (isSelectInside) {
  //     if (isPointInsidePlanes(memoPoint)) {
  //       this._currentColors[i] = color.r;
  //       this._currentColors[i + 1] = color.g;
  //       this._currentColors[i + 2] = color.b;
  //     }
  //   } else {
  //     if (!isPointInsidePlanes(memoPoint)) {
  //       this._currentColors[i] = color.r;
  //       this._currentColors[i + 1] = color.g;
  //       this._currentColors[i + 2] = color.b;
  //     }
  //   }
  // }
  // if (this._isHeatMapVisible) {
  // this._geometry.setAttribute(
  //   "color",
  //   new BufferAttribute(this._currentColors, 3)
  // );
  // this._geometry.getAttribute("color").needsUpdate = true;
  // // }
  // this._isOriginalColorUsed = false;
  // const midpoints = [];
  // const planeNormals = [];
  // const triangleIndices =[];
  // let i =0;
  // const isSelectInsides =[];
  // const isDeleteds =[];
  // for(const shape of selection.convexShapes){
  //   for(const plane of shape.planes){
  //     midpoints.push(...plane.position.toArray());
  //     planeNormals.push(...plane.normal.toArray());
  //     triangleIndices.push(i);
  //     ++i;
  //   }
  // }

  // let accumulatedShapeCount = 0;
  // for(const shapeCount of selection.shapeCounts){
  //   isSelectInsides.push(selection.convexShapes[accumulatedShapeCount].isSelectInside);
  //   isDeleteds.push(selection.convexShapes[accumulatedShapeCount].isDeleted);
  //   accumulatedShapeCount += shapeCount;
  // }
  //   this._material.shader.uniforms["shapeMidPoints"].value = midpoints;
  //   this._material.shader.uniforms["shapeMidNormals"].value = planeNormals;
  //   this._material.shader.uniforms["triangleIndices"].value = triangleIndices;
  //   this._material.shader.uniforms["shapeCounts"].value = selection.shapeCounts;
  //   this._material.shader.uniforms["highlightColor"].value = new Vector3(color.r,color.g,color.b);
  //   this._material.shader.uniforms["isSelectInsides"].value = isSelectInsides;
  //   this._material.shader.uniforms["isDeleteds"].value = isDeleteds;
  //   this._material.shader.uniforms["totalNumberOfShapes"].value = selection.shapeCounts.length;
  // }

  // public resetHighlightSelectedPointCloudSection() {
  // this._material.shader.uniforms["shapeMidPoints"].value = [];
  // this._material.shader.uniforms["shapeMidNormals"].value = [];
  // this._material.shader.uniforms["triangleIndices"].value = [];
  // this._material.shader.uniforms["numberOfTriangles"].value = 0;
  // this._material.shader.uniforms["highlightColor"].value = new Vector3(1,1,1);
  // this._material.shader.uniforms["isSelectInside"].value = true;
  // }

  public removeSelectedPoints(selectionColor: Color) {
    // if (!this._isOriginalColorUsed) {
    //   const points = this._geometry.getAttribute("position")
    //     .array as Float32Array;
    //   const filteredPoints = [];
    //   const filteredColors = [];
    //   for (let i = 0; i < this._currentColors.length; i += 3) {
    //     if (
    //       !(
    //         this._currentColors[i] === selectionColor.r &&
    //         this._currentColors[i + 1] === selectionColor.g &&
    //         this._currentColors[i + 2] === selectionColor.b
    //       )
    //     ) {
    //       filteredPoints.push(points[i], points[i + 1], points[i + 2]);
    //       filteredColors.push(this._originalColors[i], this._originalColors[i+1], this._originalColors[i+2]);
    //     }
    //   }
    //   this._geometry.setAttribute(
    //     "position",
    //     new BufferAttribute(new Float32Array(filteredPoints), 3)
    //   );
    //   this._geometry.getAttribute("position").needsUpdate = true;
    //   this._originalColors = new Float32Array(filteredColors);
    //   this._geometry.setAttribute(
    //     "color",
    //     new BufferAttribute(this._originalColors, 3)
    //   );
    //   this._geometry.getAttribute("color").needsUpdate = true;
    //   this._isOriginalColorUsed = true;
    // }
  }
}

export class PointCloudFactory {
  // private _pointCloudSettings: PointCloudSettings;
  private _raycasterSettings: RaycasterSettings;
  private _textCanvas: HTMLCanvasElement;

  constructor(
    // settings?: PointCloudSettings,
    raycasterSettings?: RaycasterSettings
  ) {
    // this._pointCloudSettings = settings;
    this._raycasterSettings = raycasterSettings;

    this._textCanvas = document.createElement("canvas");
    this._textCanvas.width = 1000;
    this._textCanvas.height = 500;
  }

  public createPointCloud(
    geometry: IPointCloudTileGeometry,
    material: PointCloudMaterial,
    tile: IPointCloudTile,
    textScale: ITextScale,
    pickMaterial?: PointCloudMaterial
  ): PointCloud {
    const pointcloud = new PointCloud(
      this._textCanvas,
      material,
      textScale,
      // this._pointCloudSettings,
      this._raycasterSettings,
      tile,
      pickMaterial
    );
    pointcloud.setGeometry(geometry.vertices, geometry.color, geometry.indices);
    return pointcloud;
  }

  // setPointSettings(settings: PointCloudSettings) {
  //   this._pointCloudSettings.size = settings.size;
  // }

  // getPointSettings() {
  //   return this._pointCloudSettings;
  // }

  // clearPointSettings() {
  //   this._pointCloudSettings.size = this._pointCloudSettings?.size || 4;
  // }
}
